import React from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '../../../shared/components';
import { Contact } from '../../contacts';
import { AssetOwner } from '../types/Asset';
import styles from './AssetLayout.module.css';

type AssetOwnersFieldProps = {
  contacts: Contact[];
  owners: AssetOwner[];
  onChange: (owners: AssetOwner[]) => void;
  error?: string;
};

const withSinglePrimary = (owners: AssetOwner[], primaryContactId?: string): AssetOwner[] => {
  if (owners.length === 0) {
    return owners;
  }

  const nextPrimary = primaryContactId && owners.some(owner => owner.contactPersonId === primaryContactId)
    ? primaryContactId
    : owners.find(owner => owner.isPrimary)?.contactPersonId || owners[0].contactPersonId;

  return owners.map(owner => ({ ...owner, isPrimary: owner.contactPersonId === nextPrimary }));
};

const contactMeta = (contact: Contact): string | undefined => {
  const parts = [contact.email, contact.phone, contact.whatsApp].map(value => value?.trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : undefined;
};

export const AssetOwnersField: React.FC<AssetOwnersFieldProps> = ({ contacts, owners, onChange, error }) => {
  const { t } = useTranslation(['assets', 'common']);
  const contactById = React.useMemo(
    () => new Map(contacts.map(contact => [contact.id, contact])),
    [contacts],
  );

  const setOwnerIds = (nextIds: string[]) => {
    const existing = new Map(owners.map(owner => [owner.contactPersonId, owner]));
    const nextOwners = nextIds.map(contactId => existing.get(contactId) ?? {
      contactPersonId: contactId,
      isRegistered: true,
      isPrimary: false,
    });
    onChange(withSinglePrimary(nextOwners));
  };

  const setRegistered = (contactId: string, isRegistered: boolean) => {
    onChange(owners.map(owner => owner.contactPersonId === contactId ? { ...owner, isRegistered } : owner));
  };

  const setPrimary = (contactId: string) => {
    onChange(withSinglePrimary(owners, contactId));
  };

  return (
    <div className={styles.field}>
      <label className={styles.label}>{t('owners', { ns: 'assets' })}</label>
      <p className={styles.hint}>{t('ownersHint', { ns: 'assets' })}</p>
      <SearchableSelect
        multiple
        value={owners.map(owner => owner.contactPersonId)}
        onChange={setOwnerIds}
        options={contacts.map(contact => ({
          value: contact.id,
          label: contact.name,
          meta: contactMeta(contact),
        }))}
        placeholder={t('searchOwners', { ns: 'assets', defaultValue: 'Search and add owners…' })}
        noOptionsText={t('noMatchingContacts', { ns: 'assets', defaultValue: 'No matching contacts' })}
        ariaLabel={t('owners', { ns: 'assets' })}
        maxSuggestions={80}
      />
      {owners.length > 0 ? (
        <div className={styles.contactList}>
          {owners.map(owner => {
            const contact = contactById.get(owner.contactPersonId);
            const name = contact?.name || owner.contactPersonId;
            return (
              <div key={owner.contactPersonId} className={styles.ownerRow}>
                <span className={styles.ownerName}>{name}</span>
                <div className={styles.ownerMeta}>
                  <label className={styles.ownerMetaItem}>
                    <input
                      type="radio"
                      name="primary-owner"
                      checked={owner.isPrimary}
                      onChange={() => setPrimary(owner.contactPersonId)}
                    />
                    {t('primaryOwner', { ns: 'assets' })}
                  </label>
                  <select
                    className={styles.ownerRoleSelect}
                    value={owner.isRegistered ? 'registered' : 'unregistered'}
                    onChange={event => setRegistered(owner.contactPersonId, event.target.value === 'registered')}
                  >
                    <option value="registered">{t('registeredOwner', { ns: 'assets' })}</option>
                    <option value="unregistered">{t('unregisteredOwner', { ns: 'assets' })}</option>
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
      {error ? <div className={styles.fieldError}>{error}</div> : null}
    </div>
  );
};

export default AssetOwnersField;
